// PER-TYPE LOOK for the realistic zombie (zombieModel.ts). Owner 2026-11-04:
// "the different zombies need to have slightly different design and colors".
//
// Every type is the SAME rigged GLB + the SAME shader program (zombieModel.ts
// patches the skin material once; types differ only in uniform VALUES, a
// shared region-mask texture and static bone offsets), so a mixed 30-zombie
// wave is still 2 draws per zombie and one compiled program pair.
//
// How the client knows the type: the server does not replicate a zombie kind,
// only the cosmetic bodyColor/glowColor/username it picks in
// GunGameRoom.spawnZombie(). Each variant's bodyColor is unique, so
// `zombieLookFor(bodyColor)` keys on it. agent/bench-zombie-types.mjs greps
// spawnZombie for those literals and fails if a colour here and there drift
// apart.
//
// Pure data + three.js math only (no DOM, no loaders) so the node bench can
// bundle it and measure every type's crown / head band offline.
import * as THREE from "three";

/** Linear-space RGB triple. */
export type RGB = readonly [number, number, number];

export interface ZombieRig {
  /** Root X/Z scale (bulk). Y is `fit`. */
  width: number;
  /** Root Y scale on top of ZOMBIE_MODEL_SCALE — measured per type by
   *  agent/bench-zombie-types.mjs so every crown stays <= 1.70 m. */
  fit: number;
  /** Clavicle length (UpperArm offset) multiplier — shoulder breadth. */
  shoulder: number;
  /** Upper-arm UNIFORM scale (thicker + longer arm incl. hand). */
  arm: number;
  /** Forearm + hand offset multiplier (limb length without thickness). */
  reach: number;
  /** Calf + foot offset multiplier (leg length). The pelvis height is
   *  animated, so longer legs raise the root by `lift` to keep feet planted. */
  leg: number;
  lift: number;
  /** Head uniform scale (a small head reads "brute"). */
  head: number;
  /** Head offset from the neck (neck length) multiplier. */
  neck: number;
  /** Chest/ribcage thickness: Spine2 scale on its two cross axes. */
  chest: number;
  /** Forward hunch (rad) added to Spine1 after every mixer step; the head
   *  gets `-hunch * lookUp` back so the face still looks ahead. */
  hunch: number;
  lookUp: number;
}

export interface ZombieLook {
  id: string;
  /** Dead-skin albedo at the baked skin's typical luminance (linear). */
  skin: RGB;
  /** x: skin recolour mix, y: mottling/bruise strength, z: char (burnt), w: wet sheen (roughness multiplier) */
  skinP: readonly [number, number, number, number];
  shirt: RGB;
  pants: RGB;
  hair: RGB;
  /** x: shirt mix, y: pants mix, z: grime, w: tear amount (0..~0.45) */
  clothP: readonly [number, number, number, number];
  blood: RGB;
  /** x: keep baked blood (0 washed out .. 1.3), y: extra gore, z: exposed ribs in tears, w: shirt pattern (0 none, 1 plaid) */
  bloodP: readonly [number, number, number, number];
  /** Emissive colour (linear) of pustules / ember cracks. */
  glow: RGB;
  /** x: glow amount, y: kind (0 pustule spots, 1 ember cracks) */
  glowP: readonly [number, number];
  /** Hair cards drawn (false = the second draw is skipped: thinner, scalp-only). */
  hairCards: boolean;
  rig: ZombieRig;
}

const RIG0: ZombieRig = { width: 1, fit: 1, shoulder: 1, arm: 1, reach: 1, leg: 1, lift: 0, head: 1, neck: 1, chest: 1, hunch: 0, lookUp: 0.6 };
const rig = (r: Partial<ZombieRig>): ZombieRig => ({ ...RIG0, ...r });
/** sRGB hex -> linear triple (authoring convenience). */
function lin(hex: string): RGB {
  const c = new THREE.Color(hex); // three's ColorManagement converts sRGB hex to linear working space
  return [c.r, c.g, c.b];
}

// Baked skin: grey-green pallor. Walkers keep it; every other type swaps it.
const WALKER_SKIN = lin("#7c8471");
const BASE: Omit<ZombieLook, "id" | "shirt" | "pants"> = {
  skin: WALKER_SKIN, skinP: [0.55, 0.35, 0, 1],
  hair: lin("#2a2a24"),
  clothP: [0.85, 0.85, 0.35, 0.08],
  blood: lin("#3c1410"), bloodP: [1, 0.05, 0, 0],
  glow: [0, 0, 0], glowP: [0, 0],
  hairCards: true,
  rig: RIG0,
};

/** Walkers: one per server rot shade, each its own outfit so a plain horde is
 *  not a clone army. Same body, same posture — they are the baseline. */
const WALKERS: Record<string, ZombieLook> = {
  "#2f5d24": { ...BASE, id: "walker", shirt: lin("#d8cfbd"), pants: lin("#9c8358"), clothP: [0, 0, 0.35, 0.08] }, // the baked outfit
  "#3a6b2a": { ...BASE, id: "walker-navy", shirt: lin("#3b4a66"), pants: lin("#3d4250"), skinP: [0.6, 0.45, 0, 1] },
  "#46603a": { ...BASE, id: "walker-flannel", shirt: lin("#8a3a2c"), pants: lin("#4a5a78"), bloodP: [1, 0.05, 0, 1] },
  "#556b2f": { ...BASE, id: "walker-olive", shirt: lin("#6b6a3e"), pants: lin("#2e2c28"), clothP: [0.85, 0.85, 0.5, 0.14] },
  "#4a5d3a": { ...BASE, id: "walker-hoodie", shirt: lin("#5d5f63"), pants: lin("#6e5a40"), hair: lin("#4a3a28") },
};

export const ZOMBIE_LOOKS: Record<string, ZombieLook> = {
  ...WALKERS,
  // RUNNER — freshly turned, still flushed: sallow flesh, soaked in bright
  // blood, sports kit. Lean, long-legged, leaning into the sprint.
  "#4a3020": {
    ...BASE, id: "runner",
    skin: lin("#a08a7a"), skinP: [0.75, 0.5, 0, 0.85],
    shirt: lin("#7a1e1e"), pants: lin("#24262c"), hair: lin("#3a2414"),
    clothP: [0.9, 0.9, 0.2, 0.12],
    blood: lin("#6a0a08"), bloodP: [1.3, 0.35, 0, 0],
    rig: rig({ fit: 0.975, width: 0.92, leg: 1.06, lift: 0.045, reach: 1.03, hunch: 0.12, chest: 0.94 }),
  },
  // BRUTE — bruised violet-grey hulk, shirt split across the chest, small head
  // on a wide, hunched frame. Broader, NOT taller.
  "#3a2440": {
    ...BASE, id: "brute",
    skin: lin("#6e5a6c"), skinP: [0.8, 0.7, 0, 1],
    shirt: lin("#3a3836"), pants: lin("#26221e"), hair: lin("#1a1612"),
    clothP: [0.9, 0.9, 0.55, 0.32],
    blood: lin("#3a0a0a"), bloodP: [1, 0.12, 0, 0],
    rig: rig({ fit: 1.023, width: 1.2, shoulder: 1.25, arm: 1.14, chest: 1.12, head: 0.9, neck: 0.85, hunch: 0.16, lookUp: 0.75 }),
  },
  // GHOUL — bloodless chalk-white, gaunt, rags hanging off it, ribs showing
  // through the tears, wisps of white hair. Thin, long-limbed, stooped.
  "#7a7f8a": {
    ...BASE, id: "ghoul",
    skin: lin("#b8bcbc"), skinP: [0.9, 0.25, 0, 1],
    shirt: lin("#5a554c"), pants: lin("#4a463e"), hair: lin("#c8c8c0"),
    clothP: [0.95, 0.95, 0.6, 0.36],
    blood: lin("#2a1210"), bloodP: [0.35, 0, 1, 0],
    hairCards: false,
    rig: rig({ fit: 1.004, width: 0.84, reach: 1.12, arm: 0.97, chest: 0.88, neck: 1.12, head: 0.96, hunch: 0.2, lookUp: 0.7 }),
  },
  // TOXIC — bloated, jaundiced-green, glowing pustules, slick with ooze.
  "#2a4a1a": {
    ...BASE, id: "toxic",
    skin: lin("#8a9a48"), skinP: [0.85, 0.55, 0, 0.55],
    shirt: lin("#a89a3a"), pants: lin("#4a4a2a"), hair: lin("#2a3018"),
    clothP: [0.8, 0.8, 0.6, 0.18],
    blood: lin("#4a5a10"), bloodP: [1, 0.3, 0, 0],
    glow: [0.35, 1.0, 0.0], glowP: [0.45, 0],
    rig: rig({ fit: 1.004, width: 1.14, chest: 1.1, arm: 1.04, head: 0.97, hunch: 0.06 }),
  },
  // LEAPER — drowned: waterlogged blue-grey skin, sodden dark clothes, the
  // blood washed out. Coiled low on long limbs.
  "#123a3a": {
    ...BASE, id: "leaper",
    skin: lin("#7a8c98"), skinP: [0.85, 0.45, 0, 0.45],
    shirt: lin("#2a4a50"), pants: lin("#1e2a30"), hair: lin("#141c1e"),
    clothP: [0.9, 0.9, 0.3, 0.16],
    blood: lin("#3a1418"), bloodP: [0.25, 0, 0, 0],
    rig: rig({ fit: 0.979, width: 0.95, leg: 1.05, lift: 0.04, reach: 1.08, hunch: 0.14, lookUp: 0.8 }),
  },
  // SPITTER — bile-yellow, chin and chest streaked with acid, long neck.
  "#4a4a12": {
    ...BASE, id: "spitter",
    skin: lin("#a89a58"), skinP: [0.8, 0.5, 0, 0.7],
    shirt: lin("#c8c0a0"), pants: lin("#5a4a32"), hair: lin("#2a2414"),
    clothP: [0.85, 0.85, 0.45, 0.12],
    blood: lin("#8a9a10"), bloodP: [1.3, 0.25, 0, 1],
    glow: [0.5, 0.6, 0.0], glowP: [0.15, 0],
    rig: rig({ fit: 0.992, neck: 1.35, width: 0.96, head: 0.97, hunch: 0.08 }),
  },
  // WRAITH — the jetpack flyer, burnt: charred black skin cracked with violet
  // embers, scorched clothes, hair burnt off.
  "#2a1a40": {
    ...BASE, id: "wraith",
    skin: lin("#4a4038"), skinP: [0.9, 0.3, 0.85, 1],
    shirt: lin("#2a2420"), pants: lin("#1e1a18"), hair: lin("#0e0c0a"),
    clothP: [0.9, 0.9, 0.7, 0.3],
    blood: lin("#1a0806"), bloodP: [0.4, 0, 0, 0],
    glow: [0.7, 0.25, 1.0], glowP: [0.6, 1],
    hairCards: false,
    rig: rig({ fit: 1.002, width: 0.9, chest: 0.92, reach: 1.05, hunch: 0.05 }),
  },
  // ABOMINATION (boss) — necrotic purple-black hide, magenta pustules, a
  // hunched mountain of a body. Wide, never taller than the 1.7 m hitbox.
  "#1a0b20": {
    ...BASE, id: "abomination",
    skin: lin("#4a3048"), skinP: [0.9, 0.8, 0.2, 0.6],
    shirt: lin("#2a1e24"), pants: lin("#1a1418"), hair: lin("#100a10"),
    clothP: [0.9, 0.9, 0.7, 0.3],
    blood: lin("#4a0a1a"), bloodP: [1.3, 0.4, 0, 0],
    glow: [1.0, 0.18, 0.75], glowP: [0.5, 0],
    hairCards: false,
    rig: rig({ fit: 1.039, width: 1.3, shoulder: 1.35, arm: 1.2, chest: 1.18, head: 0.85, neck: 0.8, hunch: 0.2, lookUp: 0.8 }),
  },
};

/** The look a zombie avatar's server bodyColor maps to. Unknown colours (a
 *  future variant, an infected player) get the baseline walker tinted by
 *  their colour, which is what every zombie looked like before this. */
export function zombieLookFor(bodyColor: string): ZombieLook {
  return ZOMBIE_LOOKS[(bodyColor || "").toLowerCase()] ?? WALKERS["#2f5d24"];
}

// ---------------------------------------------------------------- rig
/** GLTFLoader sanitises node names (spaces -> "_"). */
function bone(root: THREE.Object3D, name: string): THREE.Object3D | null {
  return root.getObjectByName(name.replace(/ /g, "_")) ?? root.getObjectByName(name) ?? null;
}

/** Per-instance bones the per-frame pose touches (null for the baseline). */
export interface RigPose { spine: THREE.Object3D; head: THREE.Object3D; qSpine: THREE.Quaternion; qHead: THREE.Quaternion }

/** Apply a type's STATIC proportions to one instance's skeleton. The clips
 *  animate only bone rotations + the pelvis translation, so bone offsets and
 *  scales set here persist through every mixer step. Call once at build. */
export function applyRig(inner: THREE.Object3D, r: ZombieRig): RigPose | null {
  const mulX = (n: string, k: number) => { const b = bone(inner, n); if (b && k !== 1) b.position.x *= k; };
  for (const s of ["L", "R"]) {
    mulX(`Bip01 ${s} UpperArm`, r.shoulder);
    mulX(`Bip01 ${s} Forearm`, r.reach); // upper-arm length
    mulX(`Bip01 ${s} Hand`, r.reach);
    mulX(`Bip01 ${s} Calf`, r.leg);
    mulX(`Bip01 ${s} Foot`, r.leg);
    const ua = bone(inner, `Bip01 ${s} UpperArm`);
    if (ua && r.arm !== 1) ua.scale.setScalar(r.arm);
  }
  mulX("Bip01 Head", r.neck);
  const head = bone(inner, "Bip01 Head");
  if (head && r.head !== 1) head.scale.setScalar(r.head);
  const chest = bone(inner, "Bip01 Spine2");
  // bone X runs along the spine: thicken across Y/Z only; the neck gets the
  // inverse so the head is not widened twice (it also gets r.head)
  if (chest && r.chest !== 1) {
    chest.scale.set(1, r.chest, r.chest);
    const neck = bone(inner, "Bip01 Neck");
    if (neck) neck.scale.set(1, 1 / r.chest, 1 / r.chest);
  }
  const spine = bone(inner, "Bip01 Spine1");
  if (!spine || !head || r.hunch === 0) return null;
  // Biped bones bend forward about their local Z (the rest neck tilt is +Z)
  return {
    spine, head,
    qSpine: new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 0, 1), r.hunch),
    qHead: new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 0, 1), -r.hunch * r.lookUp),
  };
}

/** After every mixer.update: add the type's posture on top of the clip. The
 *  mixer rewrites these quaternions from the clip each step, so this never
 *  accumulates. No allocation. */
export function poseRig(p: RigPose | null): void {
  if (!p) return;
  p.spine.quaternion.multiply(p.qSpine);
  p.head.quaternion.multiply(p.qHead);
}
